import { Box, Flex, Skeleton } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetFinancialCovenantCalculationsQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  chartsInitialEndDateMillis,
  covenantsInitialStartDateMillis,
  ExpandedFinancialCovenantsCalculation,
  PortfolioChartConfig,
  PortfolioChartFormat,
} from '../shared/portfolioChartUtils';
import { PortfolioVisibilityGroupButton } from '../shared/PortfolioVisibilityGroupButton';
import { FinancialCovenantChartTemplate } from './FinancialCovenantChartTemplate';
import { parseFinancialCovenantsFromAllCovenants } from 'packages/borrowing-base/ui/utils';
import { colors, transparency } from 'packages/common-frontend/colors';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';

const covenantsDisplayModes = ['all', 'portfolio', 'financial'] as const;
type CovenantsChartDisplayMode = typeof covenantsDisplayModes[number];
const isCovenantsChartDisplayMode = (
  test: any,
): test is CovenantsChartDisplayMode => {
  return (
    test && typeof test === 'string' && _.includes(covenantsDisplayModes, test)
  );
};

const covenantsDisplayModeQueryParam = 'display';

// Each config should be in the order that the chart components are layered
// ie. the last item is the top-most layer on render

export const createFinancialCovenantsChartConfig = (
  format: PortfolioChartFormat,
): PortfolioChartConfig<keyof ExpandedFinancialCovenantsCalculation> => {
  return {
    isPassed: {
      chartType: 'none',
      field: 'isPassed',
      label: 'Status',
      format: 'boolean',
      color: 'black',
    },
    value: {
      chartType: 'bar',
      field: 'value',
      label: 'Actual',
      format,
      color: `${colors.chart.teal}${transparency[50]}`,
      activeColor: colors.chart.teal,
      conditionalStyle: {
        condition: (datapoint: ExpandedFinancialCovenantsCalculation) => {
          return datapoint.isPassed === false;
        },
        style: { fill: colors.brick[500] },
      },
    },
    limitValue: {
      chartType: 'dashedLine',
      field: 'limitValue',
      label: 'Limit',
      format,
      color: colors.black[95],
    },
  };
};

export const CovenantsContainer: React.FC<{
  creditFacilityId: string;
}> = ({ creditFacilityId }) => {
  const history = useHistory();
  const query = useQueryParams();
  const queryDisplayMode = query.get(covenantsDisplayModeQueryParam);
  const displayMode = isCovenantsChartDisplayMode(queryDisplayMode)
    ? queryDisplayMode
    : 'all';

  const {
    data: covenantsData,
    loading: isCovenantsLoading,
  } = useGetFinancialCovenantCalculationsQuery({
    variables: {
      creditFacilityId,
      startDate: covenantsInitialStartDateMillis,
      endDate: chartsInitialEndDateMillis,
    },
  });

  if (isCovenantsLoading || !covenantsData) {
    return (
      <Skeleton height="600px" data-testid="credit-agreements-route-loading" />
    );
  }

  // once portcovs are added, update condition to no fincov && no portcov to display alert
  if (!covenantsData.financialCovenantCalculations) {
    return (
      <InfoAlertWithDescription description="No covenant calculations are available in the selected time period." />
    );
  }

  const {
    config: financialCovenantsConfig,
  } = parseFinancialCovenantsFromAllCovenants({
    allCovenantData: covenantsData,
  });

  const onSetDisplayMode = (selectedDisplayMode: CovenantsChartDisplayMode) => {
    const covenantsBaseRoute = '/portfolio/covenants';
    const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}&${covenantsDisplayModeQueryParam}=${selectedDisplayMode}`;

    history.push(`${covenantsBaseRoute}${queryParams}`);
  };

  return (
    <Box>
      <Flex mb="32px" style={{ gap: '16px' }}>
        <PortfolioVisibilityGroupButton
          selected={displayMode === 'all'}
          // @dani portfolio - add portfolio cov length when ready
          label={`Show All ${financialCovenantsConfig.length}`}
          onClick={() => onSetDisplayMode('all')}
        />
        <PortfolioVisibilityGroupButton
          selected={displayMode === 'financial'}
          label={`Financial ${financialCovenantsConfig.length}`}
          onClick={() => onSetDisplayMode('financial')}
        />
      </Flex>
      {_.map(financialCovenantsConfig, (config) => {
        const { key, valueType } = config;
        return (
          <FinancialCovenantChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle={commonUtils.toTitleCase(key)}
            covenantKey={key}
            chartConfig={createFinancialCovenantsChartConfig(valueType)}
            key={`financial-covenant-chart-${key}`}
          />
        );
      })}
    </Box>
  );
};
